import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Menu, X, ArrowRight, LayoutDashboard } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabase';
import { trackCTA } from '../../utils/metaPixel';
import { GGJLogoFull } from '../Brand/GGJLogo';
import LanguageSelector from '../LanguageSelector/LanguageSelector';

const BRAND_GRADIENT = 'var(--brand-cta-bg)';

interface NavigationProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | string) => void;
  onScrollToSection?: (sectionId: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsSignedIn(!!session?.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsSignedIn(!!session?.user);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleNavClick = (view: 'home' | 'login' | 'signup' | string) => {
    setMobileMenuOpen(false);
    if (view === 'login' || view === 'signup') {
      trackCTA(`nav_${view}`);
    }
    if (onNavigate) {
      onNavigate(view);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      {/* Sticky header – always fixed, safe-area, match home page: Logo | Nav links (desktop) | Language | Theme | Burger */}
      <nav
        className="fixed top-0 left-0 right-0 z-[100] w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm transition-colors duration-300 pt-[env(safe-area-inset-top)]"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-[52px] sm:h-16 gap-2">
            {/* Logo – Left, 44px touch target */}
            <button
              type="button"
              onClick={() => handleNavClick('home')}
              className="flex items-center flex-shrink-0 min-h-[44px] hover:opacity-90 transition-opacity touch-manipulation"
            >
              <GGJLogoFull iconSize={40} fontSize={20} />
            </button>

            {/* Center – Nav links (desktop only), match home */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <button type="button" onClick={() => handleNavClick('about-us')} className="font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
                {t('home.nav.whyGoGrabJob')}
              </button>
              <button type="button" onClick={() => handleNavClick('pricing')} className="font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
                {t('home.nav.pricing')}
              </button>
              <button type="button" onClick={() => handleNavClick('faq')} className="font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
                {t('footer.faqs')}
              </button>
              <button type="button" onClick={() => handleNavClick('contact-us')} className="font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
                {t('footer.contactSupport')}
              </button>
            </div>

            {/* Right – Language, Theme, Login + Start for free (desktop), Burger (mobile) */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <LanguageSelector
                variant="default"
                buttonClass="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              />
              <button
                type="button"
                onClick={toggleTheme}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-manipulation"
                aria-label={theme === 'dark' ? t('layout.ariaSwitchToLight') : t('layout.ariaSwitchToDark')}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              {/* Desktop: auth-aware buttons */}
              <div className="hidden lg:flex items-center gap-2">
                {isSignedIn ? (
                  <button
                    type="button"
                    onClick={() => handleNavClick('dashboard')}
                    className="min-h-[44px] px-4 py-2 text-sm font-semibold rounded-xl text-white transition-opacity hover:opacity-90 touch-manipulation flex items-center gap-1.5"
                    style={{ background: BRAND_GRADIENT }}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {t('home.nav.dashboard', 'Dashboard')}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleNavClick('login')}
                      className="min-h-[44px] px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-manipulation"
                    >
                      {t('home.nav.login')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNavClick('signup')}
                      className="min-h-[44px] px-4 py-2 text-sm font-semibold rounded-xl text-white transition-opacity hover:opacity-90 touch-manipulation flex items-center gap-1.5"
                      style={{ background: BRAND_GRADIENT }}
                    >
                      {t('home.nav.startForFree')}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-manipulation"
                aria-label={t('home.nav.menu')}
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer – same pattern as home: nav links + Login + Start for free */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] lg:hidden"
              onClick={closeMobileMenu}
              aria-hidden
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white dark:bg-gray-800 shadow-xl z-[120] lg:hidden flex flex-col pt-[env(safe-area-inset-top)] pb-[max(0.5rem,env(safe-area-inset-bottom))]"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <GGJLogoFull iconSize={32} fontSize={16} />
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg touch-manipulation -mr-1"
                  aria-label={t('layout.ariaCloseMenu')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="overflow-y-auto py-2 px-2 flex flex-col gap-0.5 shrink-0">
                <button type="button" onClick={() => handleNavClick('home')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium w-full text-left touch-manipulation">
                  {t('home.nav.whyGoGrabJob')}
                </button>
                <button type="button" onClick={() => handleNavClick('pricing')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium w-full text-left touch-manipulation">
                  {t('home.nav.pricing')}
                </button>
                <button type="button" onClick={() => handleNavClick('faq')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium w-full text-left touch-manipulation">
                  {t('footer.faqs')}
                </button>
                <button type="button" onClick={() => handleNavClick('contact-us')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium w-full text-left touch-manipulation">
                  {t('footer.contactSupport')}
                </button>
                <button type="button" onClick={() => handleNavClick('about-us')} className="min-h-[44px] flex items-center px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium w-full text-left touch-manipulation">
                  {t('footer.aboutUs')}
                </button>
              </nav>
              <div className="px-2 py-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-1.5 flex-shrink-0">
                {isSignedIn ? (
                  <button
                    type="button"
                    onClick={() => handleNavClick('dashboard')}
                    className="w-full min-h-[44px] px-3 py-2.5 text-sm font-semibold rounded-xl text-white transition-opacity hover:opacity-90 touch-manipulation flex items-center justify-center gap-1.5"
                    style={{ background: BRAND_GRADIENT }}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {t('home.nav.dashboard', 'Dashboard')}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleNavClick('login')}
                      className="w-full min-h-[44px] px-3 py-2.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors touch-manipulation"
                    >
                      {t('home.nav.login')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNavClick('signup')}
                      className="w-full min-h-[44px] px-3 py-2.5 text-sm font-semibold rounded-xl text-white transition-opacity hover:opacity-90 touch-manipulation flex items-center justify-center gap-1.5"
                      style={{ background: BRAND_GRADIENT }}
                    >
                      {t('home.nav.startForFree')}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
